import { CloudArrowUpIcon } from '@heroicons/react/20/solid';
import { PencilIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Card } from '@tremor/react';
import React, { useEffect, useState } from 'react';
import { updateMeetingRequest } from '../../Requests/MeetingRequests';
import Loader from '../Loader';
import { useTranslation } from 'react-i18next';
import { updateFragment } from '../../Requests/NewAnalysisRequests';
import DOMPurify from 'dompurify';

export default function TextSectionCard({ icon: Icon, title, content, lines, view_mode, fragment, updateFragmentState, speakerNames, ...rest }) {
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [textAreaContent, setTextAreaContent] = useState(null);
    const { t } = useTranslation()

    useEffect(() => {
        // Replace speaker identifiers with names when component mounts or content/speakerNames change
        setTextAreaContent(replaceSpeakerNames(content, speakerNames));
    }, [content, speakerNames]);

    const replaceSpeakerNames = (text, names) => {
        let replacedText = text;
        for (const key in names) {
            const name = names[key];
            replacedText = replacedText.replace(new RegExp(key, 'g'), name);
        }
        return replacedText;
    };

    const revertSpeakerNames = (text, names) => {
        let originalText = text;
        for (const key in names) {
            const name = names[key];
            originalText = originalText.replace(new RegExp(name, 'g'), key);
        }
        return originalText;
    };

    const handleSave = () => {
        setIsLoading(true);
        // Revert the speaker names to their original keys before saving
        const originalContent = revertSpeakerNames(textAreaContent, speakerNames);
        let data = { "uuid": fragment.uuid, "result": originalContent };
        updateFragment(data).then((res) => {
            updateFragmentState(fragment.uuid, originalContent);
            setIsLoading(false);
            setIsEditing(false);
        }).catch(() => {
            setIsLoading(false);
        });
    };

    return (
        <div className='text-sm py-1'>
            {textAreaContent && <Card {...rest}>
                <div className='flex justify-between items-center mb-4'>
                    <div className='flex gap-2 items-center text-lg '>
                        <Icon className='border border-black text-black bg-white rounded-md w-6 h-6 px-0.5 py-0.5' />
                        {title}
                    </div>
                    {isLoading ? <div className='border border-black w-20 bg-black text-white p-2 rounded-md gap-2 items-center shadow-md text-sm flex justify-center'>
                        <Loader />
                    </div> : (!isEditing ? (
                        (view_mode != "public") && <button className='border border-black bg-white text-black p-2 rounded-md flex gap-2 items-center shadow-md text-sm'
                            onClick={() => setIsEditing(true)}>
                            <PencilIcon className='w-4 h-4' /> {t('MeetingSection.EditLabel')}
                        </button>
                    ) : (
                        <div className='flex items-center gap-4'>
                            <button className='border border-black bg-white text-black p-2 rounded-md flex gap-2 items-center shadow-md text-sm'
                                onClick={() => setIsEditing(false)}>
                                <XMarkIcon className='w-4 h-4' /> {t('MeetingSection.CancelLabel')}
                            </button>
                            <button className='border border-black bg-black text-white p-2 rounded-md flex gap-2 items-center shadow-md text-sm'
                                onClick={handleSave}>
                                <CloudArrowUpIcon className='w-4 h-4' /> {t('MeetingSection.SaveLabel')}
                            </button>
                        </div>
                    ))}
                </div>
                {!isEditing ? (
                    textAreaContent ? <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(textAreaContent.replace(/\n/g, '<br />')) }} />
                        : <div className='min-w-full h-32 bg-gray-200 animate-pulse'></div>
                ) : (
                    <textarea
                        type="text"
                        rows={lines}
                        className="py-2 w-full text-sm"
                        value={textAreaContent}
                        onChange={(e) => setTextAreaContent(e.target.value)}
                    />
                )}
            </Card>
            }
        </div>
    );
};
